import { useServicesByProvider } from "@/hooks/api-hooks";
import { useTranslation } from "react-i18next";
import { useState, useMemo } from "react";
import { TagIcon } from "@/lib/tag-utils";
import { Select, Skeleton } from "@mantine/core";
import SearchInput from "./search-input";
import { getServiceSellPrice } from "@/lib/models-utils";
import ServiceCardPrivate from "./service-card-private";
import { todayStart } from "@/lib/format-utils";

interface Props {
    businessId: string;
    active: boolean;
}

type SortBy = 'name' | 'price';

export default function ProviderServices({
    businessId,
    active = false
}: Props) {
    const { t } = useTranslation();
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [searchText, setSearchText] = useState("");
    const [sortBy, setSortBy] = useState<SortBy>('name');

    const {
        isLoading: isLoadingServices,
        data: servicesData
    } = useServicesByProvider(businessId,
        {
            enabled: !!businessId && active
        }
    );

    const services = servicesData?.data || [];

    // Extract unique categories from services
    const categories = useMemo(() => {
        const uniqueCategories = [...new Set(services.map(s => s.category).filter(Boolean))];
        return uniqueCategories.map(category => ({
            value: category,
            label: t(category + '_plural'),
        }));
    }, [services, t]);

    // Filter and sort services by category, search text, and sort option
    const filteredServices = useMemo(() => {
        const filtered = services.filter(service => {
            const matchesCategory = !selectedCategory || service.category === selectedCategory;
            const matchesSearch = !searchText ||
                service.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                service.observations?.toLowerCase().includes(searchText.toLowerCase());
            return matchesCategory && matchesSearch;
        });

        // Sort the filtered services
        return filtered.sort((a, b) => {
            if (sortBy === 'name') {
                return (a.name || '').localeCompare(b.name || '');
            } else if (sortBy === 'price') {
                const priceA = getServiceSellPrice(a, todayStart())?.[0]?.default_amount || 0;
                const priceB = getServiceSellPrice(b, todayStart())?.[0]?.default_amount || 0;
                return priceA - priceB;
            }
            return 0;
        });
    }, [services, selectedCategory, searchText, sortBy]);

    return (
        <div className="space-y-4 max-w-screen-md">

            {/* Category filters */}
            <div className="flex gap-1 justify-between items-center overflow-x-scroll">
                <div className="flex gap-4 items-center">
                    {categories.map(category => (
                        <TagIcon
                            key={category.value}
                            plural
                            size="sm"
                            name={category.value}
                            isSelected={selectedCategory === category.value}
                            onClick={() => setSelectedCategory(category.value)}
                        />
                    ))}
                </div>
                <Select
                    radius="xl"
                    value={sortBy}
                    onChange={(value) => setSortBy(value as SortBy)}
                    data={[
                        { value: 'name', label: t('sort_by_name') },
                        { value: 'price', label: t('sort_by_price') }
                    ]}
                    size="xs"
                />
            </div>

            {/* Search input */}
            <div>
                <SearchInput
                    onSearch={setSearchText}
                    placeholder={t('search')}
                />
            </div>

            {/* Services list */}
            <div className="space-y-2">
                {isLoadingServices && (
                    <div className="space-y-2">
                        {[1, 2, 3].map(i => <Skeleton key={i} height={80} />)}
                    </div>
                )}
                {!isLoadingServices && filteredServices.length === 0 && (
                    <div className="text-center text-gray-500 py-8">
                        {t('no_services_found')}
                    </div>
                )}
                {!isLoadingServices && filteredServices.map(service => (
                    <ServiceCardPrivate
                        key={service.id}
                        service={service}
                        showProviders={true}
                        providerId={businessId}
                    />
                ))}
            </div>
        </div>
    );
}
